/**
 * CacheService.js
 * Provides frontend caching for search results to improve performance
 */

class SearchCache {
  constructor(options = {}) {
    this.cache = new Map();
    this.maxSize = options.maxSize || 50;
    this.ttl = options.ttl || 5 * 60 * 1000; // 5 minutes by default
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    // Check if item has expired
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    // Move to front (most recently used)
    this.cache.delete(key);
    this.cache.set(key, {
      ...item,
      lastAccessed: Date.now()
    });
    
    return item.value;
  }

  set(key, value) {
    // Clean up if cache is full
    if (this.cache.size >= this.maxSize) {
      this.cleanup();
    }
    
    this.cache.set(key, {
      value,
      expiry: Date.now() + this.ttl,
      lastAccessed: Date.now()
    });
  }

  cleanup() {
    // Sort by last accessed time and remove oldest entries
    const entries = [...this.cache.entries()];
    entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
    
    // Remove oldest 20% of entries
    const removeCount = Math.max(1, Math.ceil(this.maxSize * 0.2));
    entries.slice(0, removeCount).forEach(([key]) => {
      this.cache.delete(key);
    });
  }

  clear() {
    this.cache.clear();
  }

  size() {
    return this.cache.size;
  }
}

// Create singleton instances for different types of caches
export const companySearchCache = new SearchCache({ 
  maxSize: 50,
  ttl: 10 * 60 * 1000 // 10 minutes
});

export const contentSearchCache = new SearchCache({
  maxSize: 20,
  ttl: 5 * 60 * 1000 // 5 minutes
}); 